import type { SheetsClient } from '../client';
import type { CategoryRow } from '../types';
import {
  appendRow,
  genericSoftDelete,
  genericUpdate,
  newRowDefaults,
  readAllRows,
  type ListOptions,
} from './_base';
import { parseBool, parseInt10 } from '../row-utils';

const TAB = 'categories' as const;

function parse(o: Record<string, string>): CategoryRow {
  return {
    id: o['id'] ?? '',
    book_id: o['book_id'] ?? '',
    parent_id: o['parent_id'] ?? '',
    name: o['name'] ?? '',
    kind: ((o['kind'] ?? 'expense') as CategoryRow['kind']),
    color_token: o['color_token'] ?? '',
    icon: o['icon'] ?? '',
    sort_order: parseInt10(o['sort_order'], 0),
    is_system: parseBool(o['is_system']),
    rev: parseInt10(o['rev'], 0),
    created_at: o['created_at'] ?? '',
    updated_at: o['updated_at'] ?? '',
    deleted_at: (o['deleted_at'] ?? '') as CategoryRow['deleted_at'],
  };
}

/** v3 list options. `bookId` filters to system seeds (book_id='') OR
 *  `book_id === bookId`; omit it to read every category regardless of
 *  book. */
export interface CategoriesListOptions extends ListOptions {
  bookId?: string;
}

export const categoriesRepo = {
  async list(
    client: SheetsClient,
    spreadsheetId: string,
    options?: CategoriesListOptions,
  ): Promise<CategoryRow[]> {
    const rows = await readAllRows(client, spreadsheetId, TAB, options);
    const out: CategoryRow[] = [];
    for (const r of rows) {
      try {
        out.push(parse(r));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Skipping malformed category row', { id: r['id'], err });
      }
    }
    if (options?.bookId !== undefined) {
      const bid = options.bookId;
      return out.filter((c) => c.book_id === '' || c.book_id === bid);
    }
    return out;
  },

  async get(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
  ): Promise<CategoryRow | null> {
    const all = await readAllRows(client, spreadsheetId, TAB, { includeDeleted: true });
    const row = all.find((r) => r['id'] === id);
    return row ? parse(row) : null;
  },

  async create(
    client: SheetsClient,
    spreadsheetId: string,
    input: Partial<CategoryRow> & { id?: string },
  ): Promise<CategoryRow> {
    const row = newRowDefaults({
      id: input.id,
      // book_id defaults to '' meaning "system / shared seed visible
      // everywhere". User-created categories should always pass an
      // explicit value (the active book id).
      book_id: input.book_id ?? '',
      parent_id: input.parent_id ?? '',
      name: input.name ?? '',
      kind: input.kind ?? 'expense',
      color_token: input.color_token ?? '',
      icon: input.icon ?? '',
      sort_order: input.sort_order ?? 0,
      is_system: input.is_system ?? false,
    });
    await appendRow(client, spreadsheetId, TAB, row);
    return parse(row);
  },

  update(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    patch: Partial<CategoryRow>,
    expectedRev: number,
  ) {
    return genericUpdate(client, spreadsheetId, TAB, id, patch as Record<string, unknown>, expectedRev);
  },

  softDelete(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    expectedRev: number,
  ) {
    return genericSoftDelete(client, spreadsheetId, TAB, id, expectedRev);
  },
};
